import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { 
  LayoutDashboard, 
  Users, 
  Settings, 
  GraduationCap, 
  Clock, 
  ClipboardList,
  UserCircle,
  LogOut,
  ChevronDown,
  Calendar,
  Key,
  UserX,
  UserCheck,
  UserPlus,
  BarChart3,
  UserCog,
  CalendarRange,
  MessageCircle,
  Wallet,
  Wrench,
  AlertTriangle,
  ShieldCheck
} from "lucide-react";

export default function Navigation() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Admin navigation items
  const adminNavItems = [
    { to: "/", label: "Dashboard", icon: LayoutDashboard },
    { to: "/students", label: "Students", icon: Users },
    { to: "/student-accounts", label: "Student Accounts", icon: Key },
    { to: "/inactive-students", label: "Inactive Students", icon: UserX },
    { to: "/active-students", label: "Active Students", icon: UserCheck },
    { to: "/teachers", label: "Payroll", icon: GraduationCap },
    { to: "/time-management", label: "Time Clock", icon: Clock },
    { to: "/schedule", label: "Schedules", icon: Calendar },
    { to: "/admin/untagged-alerts", label: "Alerts", icon: AlertTriangle },
    { to: "/admin/data-integrity", label: "Data Integrity", icon: ShieldCheck },
    { to: "/admin/schedule-repair", label: "Schedule Repair", icon: Wrench },
    { to: "/admin-tutor-profiles", label: "Tutor Profiles", icon: UserCog },
    { to: "/tutor-messages", label: "Messages", icon: MessageCircle },
    { to: "/recruitment", label: "Recruitment", icon: UserPlus },
    { to: "/trial-bookings", label: "Trial Bookings", icon: Calendar },
    { to: "/admin-payments", label: "Payments", icon: BarChart3 },
    { to: "/sales-analytics", label: "Sales Analytics", icon: BarChart3 },
    { to: "/users", label: "Users", icon: UserCircle },
    { to: "/settings", label: "Settings", icon: Settings },
  ];

  // Tutor navigation items
  const tutorNavItems = [
    { to: "/schedule", label: "My Schedule", icon: Calendar },
    { to: "/my-availability", label: "My Availability", icon: CalendarRange },
    { to: "/my-salary", label: "My Salary", icon: Wallet },
    { to: "/tutor-messages", label: "Messages", icon: MessageCircle },
    { to: "/time-clock", label: "Time Clock", icon: Clock },
  ];

  const navItems = user?.role === "admin" ? adminNavItems : tutorNavItems;

  return (
    <nav className="bg-white border-b border-slate-200 sticky top-0 z-50" data-testid="navigation">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-8">
            <h1 className="text-xl font-bold text-slate-900 tracking-tight" data-testid="app-title">
              ESL Talks
            </h1>
            <div className="hidden md:flex items-center gap-1">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  end={item.to === "/" || item.to === "/time-clock"}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-indigo-50 text-indigo-700"
                        : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
                    }`
                  }
                  data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </NavLink>
              ))}
            </div>
          </div>
          
          {/* User Menu */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                  <span className="text-indigo-700 font-semibold text-sm">
                    {user?.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                  </span>
                </div>
                <span className="hidden sm:inline text-slate-700">{user?.name}</span>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">{user?.email}</p>
                <p className="text-xs text-indigo-600 capitalize mt-1">{user?.role}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600 cursor-pointer" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
      
      {/* Mobile nav */}
      <div className="md:hidden border-t border-slate-100 px-4 py-2 flex gap-2 overflow-x-auto">
        {navItems.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === "/" || item.to === "/time-clock"}
            className={({ isActive }) =>
              `flex items-center gap-1 px-3 py-2 rounded-md text-xs font-medium transition-colors whitespace-nowrap ${
                isActive
                  ? "bg-indigo-50 text-indigo-700"
                  : "text-slate-600 hover:bg-slate-100"
              }`
            }
            data-testid={`nav-mobile-${item.label.toLowerCase().replace(' ', '-')}`}
          >
            <item.icon className="w-4 h-4" />
            {item.label}
          </NavLink>
        ))}
      </div>
    </nav>
  );
}
